/* eslint-disable no-underscore-dangle */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Badge, Button, ButtonGroup, Card, Dropdown, Form, Spinner, Table,
} from 'react-bootstrap';
import _ from 'lodash';
import dayjs from 'dayjs';

import Error from '../../Components/Error';
import PaymentsService from '../../Services/PaymentsService';
import { useAccountsList } from '../../Hooks/useAccountsCache';
import { formatDate } from '../../Utils/timezone';
import { useCounterpartyNames } from '../../Hooks/useCounterpartyNames';
import EditAllocationModal from './Components/EditAllocationModal';
import LinkToBookingModal from './Components/LinkToBookingModal';
import PaymentDetailsModal from './Components/PaymentDetailsModal';
import RecordPaymentModal from './Components/RecordPaymentModal';
import BulkPaymentModal from './Components/BulkPaymentModal';
import paymentBookingAllocations from './Components/paymentBookingAllocations';
import RefundPaymentModal from '../Orders/Components/RefundPaymentModal';
import useDocumentIssuer from '../../Components/Billingo/useDocumentIssuer';
import { INVOICED_DOC_TYPES, isLiveDocument } from '../../Utils/billingoDocuments';
import isInvoicingEnabled from '../../Utils/partnerInvoicing';
import { formatPaymentAmount } from './Components/paymentDisplay';
import { isRefundable, refundedOriginalIds } from '../../Utils/paymentStatus';

const PAYMENT_STATUS_VARIANTS = {
  completed: 'success',
  pending: 'warning',
  failed: 'secondary',
  refunded: 'danger',
  reconciled: 'info',
};

const STATUSES = ['pending', 'completed', 'failed', 'refunded', 'reconciled'];
const DIRECTIONS = ['in', 'out'];
const COUNTERPARTY_KINDS = ['booking', 'partner', 'operator', 'system'];

// Keys that are mirrored into the URL query string for shareable
// links. Anything else (e.g. internal UI flags) is kept in component
// state only. NOTE: `method` was replaced by `account_id` when the
// Accounts model landed — see the LOCKED backend contract.
const URL_FILTER_KEYS = [
  'status', 'direction', 'account_id',
  'counterparty_kind', 'counterparty_id',
  'booking_id', 'from', 'to', 'q', 'page', 'limit',
];

// Truncate from the END so the most distinctive (right-hand) portion of
// an ID/ref stays visible. Used for counterparty IDs and external refs.
function truncateLeft(s, max = 12) {
  if (!s) return '';
  if (s.length <= max) return s;
  return `…${s.slice(-max)}`;
}

// Pick the most distinctive external ref to surface in the table — in
// priority order: stripe_charge_id > bank_reference > stripe_payout_id
// > barion_payment_id. Returns { label, full } or null.
function pickExternalRef(p) {
  const er = p?.external_refs;
  if (!er) return null;
  if (er.stripe_charge_id) return { label: 'stripe', full: er.stripe_charge_id };
  if (er.bank_reference) return { label: 'bank', full: er.bank_reference };
  if (er.stripe_payout_id) return { label: 'payout', full: er.stripe_payout_id };
  if (er.barion_payment_id) return { label: 'barion', full: er.barion_payment_id };
  return null;
}

// Read filters from the URL query string. Returns an object whose
// shape matches what PaymentsService.list expects. Numeric fields
// (page, limit) are coerced; everything else is a string.
function readFiltersFromUrl(searchParams) {
  const out = {};
  URL_FILTER_KEYS.forEach((k) => {
    const v = searchParams.get(k);
    if (v == null || v === '') return;
    if (k === 'page' || k === 'limit') {
      const n = Number(v);
      if (Number.isFinite(n) && n > 0) out[k] = n;
    } else {
      out[k] = v;
    }
  });
  return out;
}

// Quick-filter presets. Clicking one of these overrides the current
// filter set with the preset payload (i.e. previous filters are
// cleared — chips are not additive). The "All" preset is implicit:
// clicking "Reset" clears everything.
function buildQuickPresets() {
  return [
    { id: 'all', label: 'All', filters: {} },
    {
      id: 'unmatched',
      label: 'Unmatched',
      filters: { counterparty_kind: 'system', counterparty_id: 'stripe-unmatched' },
    },
    {
      id: 'recent',
      label: 'Recent',
      filters: { from: dayjs().subtract(30, 'day').format('YYYY-MM-DD') },
    },
    { id: 'refunded', label: 'Refunded', filters: { status: 'refunded' } },
    { id: 'pending', label: 'Pending', filters: { status: 'pending' } },
    { id: 'failed', label: 'Failed', filters: { status: 'failed' } },
  ];
}

// True if `current` matches `preset.filters` exactly (over the keys
// the preset cares about). The "All" preset matches when no filters
// from any preset are set — handled separately by the caller.
function presetMatches(preset, current) {
  const keys = Object.keys(preset.filters);
  if (keys.length === 0) return false; // handled separately
  return keys.every((k) => String(current[k] ?? '') === String(preset.filters[k]));
}

function Payments() {
  const [searchParams, setSearchParams] = useSearchParams();

  // Pull the accounts list once via the module-level cache hook. Used
  // both by the filter <Form.Select> and to resolve `account_id` →
  // account name in the table column. We don't gate the table render
  // on `accountsLoading` — payments load independently and account
  // names just show "—" until the cache resolves.
  const { accounts, byId: accountsById } = useAccountsList();
  const { partnersById, operatorsById } = useCounterpartyNames();

  // Initial state: read filters from the URL so deep links / preset
  // links from Header.jsx land on the page with the right filter
  // pre-applied. Defaulted to { page: 1 } so the request is well-formed
  // even before the user touches anything.
  const [filters, setFilters] = useState(() => ({
    page: 1,
    ...readFiltersFromUrl(searchParams),
  }));
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '');

  const [payments, setPayments] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1, limit: 50, total: 0, pages: 1,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [selectedPayment, setSelectedPayment] = useState(null);
  const [refundingPayment, setRefundingPayment] = useState(null);
  const [linkingPayment, setLinkingPayment] = useState(null);
  const [editingAllocationFor, setEditingAllocationFor] = useState(null);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);

  // Sync filters -> URL. We strip undefined/empty so the URL stays
  // clean when filters get cleared. page=1 is omitted by convention
  // (the canonical URL for "no filters" is /payments, not
  // /payments?page=1).
  useEffect(() => {
    const next = {};
    URL_FILTER_KEYS.forEach((k) => {
      const v = filters[k];
      if (v == null || v === '') return;
      if (k === 'page' && Number(v) === 1) return;
      next[k] = String(v);
    });
    // Avoid a useless setSearchParams call if nothing changed — that
    // would cause an extra history entry and a redundant re-render.
    const currentEntries = Array.from(searchParams.entries()).sort();
    const nextEntries = Object.entries(next).sort();
    const same = currentEntries.length === nextEntries.length
      && currentEntries.every(([k, v], i) => nextEntries[i][0] === k && nextEntries[i][1] === v);
    if (!same) {
      setSearchParams(next, { replace: true });
    }
  }, [filters]);

  // Fetch on filter change. The list endpoint accepts ALL the same
  // filter keys we mirror into the URL, so we just pass the state
  // through (the service drops undefined/empty values).
  const fetchRequestSeqRef = useRef(0);
  const fetchPayments = useCallback(async () => {
    const mySeq = fetchRequestSeqRef.current + 1;
    fetchRequestSeqRef.current = mySeq;
    setIsLoading(true);
    setError('');
    try {
      const envelope = await PaymentsService.list(filters);
      if (mySeq !== fetchRequestSeqRef.current) return; // stale response
      setPayments(Array.isArray(envelope?.data) ? envelope.data : []);
      setPagination(envelope?.pagination || {
        page: 1, limit: 50, total: 0, pages: 1,
      });
    } catch (err) {
      if (mySeq !== fetchRequestSeqRef.current) return;
      setError(err.response?.data?.message || err.message || 'Failed to load payments');
      setPayments([]);
    } finally {
      if (mySeq === fetchRequestSeqRef.current) setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  // Debounced "q" handler — text input gets a 300ms delay before it
  // mutates `filters` (and triggers a refetch); selects/dates apply
  // instantly. Memoized so the same debounce instance is reused.
  const debouncedSetQuery = useMemo(
    () => _.debounce((v) => {
      setFilters((f) => ({ ...f, q: v || undefined, page: 1 }));
    }, 300),
    [],
  );
  useEffect(() => () => debouncedSetQuery.cancel(), [debouncedSetQuery]);

  const handleSearchChange = (e) => {
    const v = e.target.value;
    setSearchInput(v);
    debouncedSetQuery(v);
  };

  const setField = (key, value) => {
    setFilters((f) => {
      const next = { ...f, page: 1 };
      if (value == null || value === '') {
        delete next[key];
      } else {
        next[key] = value;
      }
      return next;
    });
  };

  const handleResetFilters = () => {
    setFilters({ page: 1 });
    setSearchInput('');
    debouncedSetQuery.cancel();
  };

  const handleQuickPreset = (preset) => {
    debouncedSetQuery.cancel();
    if (preset.id === 'all') {
      handleResetFilters();
      return;
    }
    // Presets are not additive — they replace the current filter set.
    // Reasoning: chips are meant to be one-click "show me X"
    // shortcuts; mixing them with whatever was already filtered
    // produces surprising results.
    setFilters({ page: 1, ...preset.filters });
    setSearchInput(preset.filters.q || '');
  };

  const presets = useMemo(buildQuickPresets, []);
  const hasAnyPresetActive = presets
    .filter((p) => p.id !== 'all')
    .some((p) => presetMatches(p, filters));

  const refundedIds = useMemo(() => refundedOriginalIds(payments), [payments]);

  const handlePaymentUpdated = (updated) => {
    if (!updated?._id) return;
    setPayments((rows) => rows.map((r) => (r._id === updated._id ? updated : r)));
    setSelectedPayment(updated);
  };

  const handleRefunded = () => {
    // After a refund the list needs to refresh: a new offset row is
    // inserted and the original's status flips. Re-fetching is the
    // simplest correct path (no risk of out-of-order rows).
    setRefundingPayment(null);
    setSelectedPayment(null);
    fetchPayments();
  };

  const handleLinked = () => {
    setLinkingPayment(null);
    setSelectedPayment(null);
    fetchPayments();
  };

  // Re-allocating only rewrites the payment's lines[] — no offset row is
  // inserted and occurred_at never moves — so unlike handleRefunded /
  // handleLinked there is nothing a refetch would reveal. Patch the row
  // with the echoed payment (same approach as handlePaymentUpdated) so
  // the split hint in the Counterparty column refreshes in place.
  const handleAllocationEdited = (updated) => {
    setEditingAllocationFor(null);
    if (!updated?._id) return;
    setPayments((rows) => rows.map((r) => (r._id === updated._id ? updated : r)));
  };

  // After a manual Record Payment we re-fetch the list so the new row
  // shows up in the correct sorted position (the server returns
  // payments sorted by occurred_at desc, and inserting the echoed row
  // at index 0 would put it ahead of any older payment recorded with
  // a backdated occurred_at).
  // After a booking-counterparty payment is recorded, every payment should have
  // an advance/final invoice behind it. If none exists, prompt to create one
  // (prefilled from the payment) — without auto-issuing anything.
  const documentIssuer = useDocumentIssuer();

  const maybePromptInvoice = (created) => {
    if (!created || created.direction !== 'in') return;
    if (created.counterparty?.kind !== 'booking' || !created.counterparty?.id) return;
    documentIssuer.issue({
      group_key: created._id,
      booking_ids: [created.counterparty.id],
      payment: created,
      // Only for per-trip invoicing (monthly partners get no per-booking
      // documents), and only while nothing is invoiced yet.
      skip: ([booking], docs) => !isInvoicingEnabled(booking.partner)
        || docs.some((d) => isLiveDocument(d) && INVOICED_DOC_TYPES.includes(d.type)),
    });
  };

  const handleRecorded = (created) => {
    setShowRecordModal(false);
    fetchPayments();
    maybePromptInvoice(created);
  };

  // Same refresh strategy as Record Payment — the bulk endpoint also
  // returns the new Payment, but we re-fetch so the row lands in its
  // correct sorted position.
  const handleBulkRecorded = () => {
    setShowBulkModal(false);
    fetchPayments();
  };

  const total = pagination?.total ?? 0;
  const page = pagination?.page ?? 1;
  const pages = pagination?.pages ?? 1;
  const limit = pagination?.limit ?? 50;
  const rangeFrom = total === 0 ? 0 : (page - 1) * limit + 1;
  const rangeTo = Math.min(total, page * limit);

  const goToPage = (nextPage) => {
    if (nextPage < 1 || nextPage > pages) return;
    setFilters((f) => ({ ...f, page: nextPage }));
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h1 className="page-title mb-0">Payments</h1>
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            onClick={() => setShowBulkModal(true)}
          >
            <i className="fa fa-layer-group me-2" />
            Bulk Payment
          </Button>
          <Button
            variant="primary"
            onClick={() => setShowRecordModal(true)}
          >
            <i className="fa fa-plus me-2" />
            Record Payment
          </Button>
        </div>
      </div>

      <Error error={error} setError={setError} />
      <Error error={documentIssuer.error} setError={documentIssuer.setError} />

      {/* Filter card. Sticky-ish via Bootstrap "shadow-sm" + top
          margin; matches the visual density of the Orders page
          filters. */}
      <Card className="mb-3 shadow-sm">
        <Card.Body>
          {/* Quick preset chips */}
          <div className="d-flex flex-wrap gap-2 mb-3">
            {presets.map((p) => {
              const active = p.id === 'all'
                ? !hasAnyPresetActive
                : presetMatches(p, filters);
              return (
                <Button
                  key={p.id}
                  size="sm"
                  variant={active ? 'primary' : 'outline-secondary'}
                  onClick={() => handleQuickPreset(p)}
                >
                  {p.label}
                </Button>
              );
            })}
            <div className="ms-auto">
              <Button
                size="sm"
                variant="outline-danger"
                onClick={handleResetFilters}
                disabled={Object.keys(filters).filter((k) => k !== 'page').length === 0}
              >
                <i className="fa fa-times me-1" />
                Reset
              </Button>
            </div>
          </div>

          <div className="row g-2">
            <Form.Group controlId="pf_from" className="col-md-3 col-sm-6">
              <Form.Label className="small text-muted mb-1">From</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={filters.from || ''}
                onChange={(e) => setField('from', e.target.value)}
              />
            </Form.Group>
            <Form.Group controlId="pf_to" className="col-md-3 col-sm-6">
              <Form.Label className="small text-muted mb-1">To</Form.Label>
              <Form.Control
                type="date"
                size="sm"
                value={filters.to || ''}
                onChange={(e) => setField('to', e.target.value)}
              />
            </Form.Group>
            <Form.Group controlId="pf_direction" className="col-md-2 col-sm-6">
              <Form.Label className="small text-muted mb-1">Direction</Form.Label>
              <Form.Select
                size="sm"
                value={filters.direction || ''}
                onChange={(e) => setField('direction', e.target.value)}
              >
                <option value="">All</option>
                {DIRECTIONS.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group controlId="pf_account_id" className="col-md-2 col-sm-6">
              <Form.Label className="small text-muted mb-1">Account</Form.Label>
              <Form.Select
                size="sm"
                value={filters.account_id || ''}
                onChange={(e) => setField('account_id', e.target.value)}
              >
                <option value="">All</option>
                {accounts.map((a) => (
                  <option key={a._id} value={a._id}>
                    {a.name}
                    {a.is_active ? '' : ' (inactive)'}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group controlId="pf_status" className="col-md-2 col-sm-6">
              <Form.Label className="small text-muted mb-1">Status</Form.Label>
              <Form.Select
                size="sm"
                value={filters.status || ''}
                onChange={(e) => setField('status', e.target.value)}
              >
                <option value="">All</option>
                {STATUSES.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group controlId="pf_counterparty_kind" className="col-md-3 col-sm-6">
              <Form.Label className="small text-muted mb-1">Counterparty kind</Form.Label>
              <Form.Select
                size="sm"
                value={filters.counterparty_kind || ''}
                onChange={(e) => setField('counterparty_kind', e.target.value)}
              >
                <option value="">All</option>
                {COUNTERPARTY_KINDS.map((k) => (
                  <option key={k} value={k}>{k}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group controlId="pf_q" className="col-md-9 col-sm-6">
              <Form.Label className="small text-muted mb-1">Search</Form.Label>
              <Form.Control
                type="text"
                size="sm"
                placeholder="Search by Stripe ID, bank ref, booking ref, notes..."
                value={searchInput}
                onChange={handleSearchChange}
              />
            </Form.Group>
          </div>
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="text-muted small">
          {isLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Loading…
            </>
          ) : (
            <>
              Showing
              {' '}
              <strong>{rangeFrom}</strong>
              –
              <strong>{rangeTo}</strong>
              {' of '}
              <strong>{total}</strong>
              {' '}
              payment
              {total === 1 ? '' : 's'}
            </>
          )}
        </div>
        <Button
          size="sm"
          variant="outline-secondary"
          onClick={fetchPayments}
          disabled={isLoading}
          aria-label="Refresh"
        >
          <i className={`fa fa-sync ${isLoading ? 'fa-spin' : ''}`} />
        </Button>
      </div>

      <div className="table-responsive">
        <Table hover size="sm" className="align-middle responsive-card-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Counterparty</th>
              <th>Direction</th>
              <th>Account</th>
              <th>Status</th>
              <th className="text-end">Gross</th>
              <th className="text-end">Fee</th>
              <th className="text-end">Net</th>
              <th>External Refs</th>
              <th>Notes</th>
              <th style={{ width: '220px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {payments.length === 0 && !isLoading && (
              <tr>
                <td colSpan={11} className="text-center text-muted py-5">
                  <i className="fa fa-inbox d-block mb-2" style={{ fontSize: '1.5rem' }} />
                  No payments match these filters.
                </td>
              </tr>
            )}
            {payments.map((p) => {
              const isOffset = !!p?.parent_payment_id;
              const refundable = isRefundable(p, refundedIds);
              const canLink = p?.counterparty?.kind === 'system';
              // Natural complement of canLink: a payment already tied to a
              // booking can be re-allocated across its order lines, an
              // unmatched one has to be linked first. So a row offers Link
              // or Edit, never both.
              const canEditAllocation = refundable && p?.counterparty?.kind === 'booking';
              const ref = pickExternalRef(p);
              return (
                <tr
                  key={p._id}
                  className={isOffset ? 'table-light' : undefined}
                >
                  <td data-label="Date">
                    {isOffset && (
                      <span
                        className="me-1 text-muted"
                        title="Refund offset of an earlier payment"
                      >
                        ↪
                      </span>
                    )}
                    {p.occurred_at ? formatDate(p.occurred_at) : '—'}
                  </td>
                  <td data-label="Counterparty">
                    <small>
                      {(() => {
                        const kind = p.counterparty?.kind;
                        const id = p.counterparty?.id;
                        if (!kind || !id) return '—';
                        if (kind === 'partner') {
                          const partner = partnersById.get(id);
                          const name = partner?.name || partner?.Name;
                          return name ? (
                            <span title={id}>
                              <span className="text-muted me-1">partner:</span>
                              {name}
                            </span>
                          ) : (
                            <span>
                              <span className="text-muted me-1">partner:</span>
                              <code title={id}>{truncateLeft(id, 8)}</code>
                            </span>
                          );
                        }
                        if (kind === 'operator') {
                          const op = operatorsById.get(id);
                          const name = op?.Name || op?.name;
                          return name ? (
                            <span title={id}>
                              <span className="text-muted me-1">operator:</span>
                              {name}
                            </span>
                          ) : (
                            <span>
                              <span className="text-muted me-1">operator:</span>
                              <code title={id}>{truncateLeft(id, 8)}</code>
                            </span>
                          );
                        }
                        if (kind === 'system') {
                          return (
                            <span className="text-muted">{id}</span>
                          );
                        }
                        // booking — prefer the enriched booking_reference
                        // (PU_DASH_xx) when the API resolved the booking;
                        // fall back to the truncated id for deleted/legacy
                        // rows where lookup yielded nothing.
                        const bref = p.counterparty?.booking_reference;
                        const bname = p.counterparty?.booking_full_name;
                        // A split payment names only its first booking as
                        // counterparty — flag the rest so it isn't read as 1:1.
                        const others = paymentBookingAllocations(p)
                          .filter((a) => a.booking_id !== String(id));
                        const more = others.length > 0 && (
                          <span
                            className="text-muted ms-1"
                            title={others.map((a) => a.booking_reference || a.booking_id).join(', ')}
                          >
                            {`+${others.length} more`}
                          </span>
                        );
                        if (bref) {
                          return (
                            <span title={id}>
                              <span className="text-muted me-1">booking:</span>
                              <code>{bref}</code>
                              {bname && (
                                <span className="text-muted ms-1 small">{bname}</span>
                              )}
                              {more}
                            </span>
                          );
                        }
                        return (
                          <span>
                            <span className="text-muted me-1">booking:</span>
                            <code title={id}>{truncateLeft(id, 8)}</code>
                            {more}
                          </span>
                        );
                      })()}
                    </small>
                  </td>
                  <td data-label="Direction">
                    <Badge
                      bg={p.direction === 'in' ? 'success' : 'danger'}
                      pill
                    >
                      {p.direction || '—'}
                    </Badge>
                  </td>
                  <td data-label="Account">
                    {(() => {
                      const acc = p.account_id ? accountsById.get(p.account_id) : null;
                      if (acc) {
                        return (
                          <span title={acc.identifier || acc.provider || ''}>
                            {acc.name}
                          </span>
                        );
                      }
                      return <span className="text-muted">—</span>;
                    })()}
                  </td>
                  <td data-label="Status">
                    {p.status ? (
                      <Badge bg={PAYMENT_STATUS_VARIANTS[p.status] || 'secondary'}>
                        {p.status}
                      </Badge>
                    ) : <span className="text-muted">—</span>}
                  </td>
                  <td className="text-end" data-label="Gross">{formatPaymentAmount(p.gross_amount, p.currency)}</td>
                  <td className="text-end" data-label="Fee">{formatPaymentAmount(p.fee, p.currency)}</td>
                  <td className="text-end fw-bold" data-label="Net">
                    {formatPaymentAmount(p.net_amount, p.currency)}
                  </td>
                  <td data-label="External Refs">
                    {ref ? (
                      <code title={ref.full}>
                        {ref.full.length > 12 ? `${ref.full.slice(0, 12)}…` : ref.full}
                      </code>
                    ) : <span className="text-muted">—</span>}
                  </td>
                  <td data-label="Notes">
                    <small className="text-muted" title={p.notes || ''}>
                      {(() => {
                        if (!p.notes) return '—';
                        return p.notes.length > 40 ? `${p.notes.slice(0, 40)}…` : p.notes;
                      })()}
                    </small>
                  </td>
                  <td>
                    {/* Desktop: inline buttons. Mobile: kebab menu. */}
                    <div className="d-none d-md-flex flex-wrap gap-1">
                      <Button
                        size="sm"
                        variant="outline-primary"
                        onClick={() => setSelectedPayment(p)}
                      >
                        Details
                      </Button>
                      {refundable && (
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => setRefundingPayment(p)}
                        >
                          Refund
                        </Button>
                      )}
                      {canLink && (
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => setLinkingPayment(p)}
                        >
                          <i className="fa fa-link" />
                        </Button>
                      )}
                      {canEditAllocation && (
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => setEditingAllocationFor(p)}
                          title="Edit which order line(s) this payment is allocated to"
                        >
                          <i className="fa fa-pencil me-1" />
                          Edit
                        </Button>
                      )}
                    </div>
                    <Dropdown align="end" className="d-md-none">
                      <Dropdown.Toggle size="sm" variant="outline-secondary" id={`pay-actions-${p._id}`}>
                        <i className="fa fa-ellipsis-v" />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => setSelectedPayment(p)}>
                          Details
                        </Dropdown.Item>
                        {refundable && (
                          <Dropdown.Item onClick={() => setRefundingPayment(p)}>
                            Refund
                          </Dropdown.Item>
                        )}
                        {canLink && (
                          <Dropdown.Item onClick={() => setLinkingPayment(p)}>
                            <i className="fa fa-link me-2" />
                            Link
                          </Dropdown.Item>
                        )}
                        {canEditAllocation && (
                          <Dropdown.Item onClick={() => setEditingAllocationFor(p)}>
                            <i className="fa fa-pencil me-2" />
                            Edit allocation
                          </Dropdown.Item>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      {pages > 1 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div className="text-muted small">
            Page
            {' '}
            <strong>{page}</strong>
            {' of '}
            <strong>{pages}</strong>
          </div>
          <ButtonGroup size="sm">
            <Button
              variant="outline-secondary"
              onClick={() => goToPage(page - 1)}
              disabled={page <= 1 || isLoading}
            >
              <i className="fa fa-chevron-left me-1" />
              Previous
            </Button>
            <Button
              variant="outline-secondary"
              onClick={() => goToPage(page + 1)}
              disabled={page >= pages || isLoading}
            >
              Next
              <i className="fa fa-chevron-right ms-1" />
            </Button>
          </ButtonGroup>
        </div>
      )}

      <PaymentDetailsModal
        show={!!selectedPayment}
        onHide={() => setSelectedPayment(null)}
        payment={selectedPayment}
        relatedPayments={payments}
        onUpdated={handlePaymentUpdated}
        onRefund={(p) => { setSelectedPayment(null); setRefundingPayment(p); }}
        onLinkToBooking={(p) => { setSelectedPayment(null); setLinkingPayment(p); }}
        onEditAllocation={(p) => { setSelectedPayment(null); setEditingAllocationFor(p); }}
      />

      <RefundPaymentModal
        show={!!refundingPayment}
        onHide={() => setRefundingPayment(null)}
        payment={refundingPayment}
        onRefunded={handleRefunded}
      />

      <LinkToBookingModal
        show={!!linkingPayment}
        onHide={() => setLinkingPayment(null)}
        payment={linkingPayment}
        onLinked={handleLinked}
      />

      <EditAllocationModal
        show={!!editingAllocationFor}
        onHide={() => setEditingAllocationFor(null)}
        payment={editingAllocationFor || {}}
        onEdited={handleAllocationEdited}
      />

      <RecordPaymentModal
        show={showRecordModal}
        onHide={() => setShowRecordModal(false)}
        onRecorded={handleRecorded}
      />

      <BulkPaymentModal
        show={showBulkModal}
        onHide={() => setShowBulkModal(false)}
        onRecorded={handleBulkRecorded}
      />

      {documentIssuer.modal}
    </>
  );
}

export default Payments;
